import type { Narrow, Schema } from './schemas';

/**
 * Where a firm's maps open, resolved once for every surface.
 *
 * Four maps used to decide this for themselves — the desk from the widest zone it could
 * read, the zone editor from the deployment's one configured centre, the passenger app
 * from a hardcoded Sofia — and a new firm's first morning was four different answers, none
 * of them its city. The API now answers with the firm's own view and where it came from
 * (`mapHome` on `/api/tenant/current`); this is the one place that turns that answer, and
 * what the surface knows on its own, into a centre and a zoom.
 *
 * The order: the view the firm chose, then the one derived from its widest zone, then the
 * deployment's configured centre, then nowhere in particular — a map of Europe, so that an
 * unconfigured firm looks unconfigured rather than wrong. A wrong city looks like a working
 * map with the drivers missing, which is worse than an obviously unset one.
 *
 * A pure function on purpose, tested in `tests/js`: the precedence is the only rule here,
 * and a rule with four inputs earns a test rather than a paragraph at each call site.
 */

/** `set` by the firm, `derived` from its widest zone, or `none`. */
export type MapHomeSource = 'set' | 'derived' | 'none';

/** What the API says about where this firm's maps open. */
export type MapHome = Narrow<Schema<'MapHomeView'>, { source: MapHomeSource }>;

/** The deployment's one configured centre, as the map style carries it. */
export interface DeploymentCentre {
  defaultLongitude: number | null;
  defaultLatitude: number | null;
}

/** Where a map should open, and why there. */
export interface MapView {
  longitude: number;
  latitude: number;
  zoom: number;
  /** Which of the four answers won, so a screen can say so. */
  source: MapHomeSource | 'deployment' | 'nowhere';
}

/**
 * The centre and zoom a map opens on.
 *
 * @param home What the API said, or nothing — a document cached before the field existed,
 *   or a request that failed. Either reads as a firm that has said nothing.
 * @param deployment The installation's configured centre, or nothing.
 * @param own The surface's own zoom for a view that carries none — a desk wants a city,
 *   a pickup map wants a street — and where it opens when nothing at all is known.
 */
export function resolveMapView(
  home: MapHome | null | undefined,
  deployment: DeploymentCentre | null | undefined,
  own: { zoom: number; nowhere: { longitude: number; latitude: number; zoom: number } },
): MapView {
  if (
    home !== null &&
    home !== undefined &&
    home.source !== 'none' &&
    home.longitude !== null &&
    home.latitude !== null
  ) {
    return {
      longitude: home.longitude,
      latitude: home.latitude,
      // A set view carries the zoom the administrator saw when they pressed save. A
      // derived one carries none, and the surface's own is the right one.
      zoom: home.source === 'set' && home.zoom !== null ? home.zoom : own.zoom,
      source: home.source,
    };
  }

  if (
    deployment !== null &&
    deployment !== undefined &&
    deployment.defaultLongitude !== null &&
    deployment.defaultLatitude !== null
  ) {
    return {
      longitude: deployment.defaultLongitude,
      latitude: deployment.defaultLatitude,
      zoom: own.zoom,
      source: 'deployment',
    };
  }

  return { ...own.nowhere, source: 'nowhere' };
}
